import { Link, Navigate, useParams } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import { MarketingPageLayout } from "@/components/marketing/MarketingPageLayout";
import { GetStashtCta } from "@/components/marketing/GetStashtCta";
import { PageMeta } from "@/components/seo/PageMeta";
import { blogPostingJsonLd, breadcrumbJsonLd, faqPageJsonLd } from "@/lib/structuredData";
import { BLOG_OG_IMAGE } from "@/lib/seo";
import {
  formatPostDate,
  getCategoryBySlug,
  getPostBySlug,
  getRelatedPosts,
  type BlogPost,
} from "@/lib/blog";
import { BLOG, BLOG_CATEGORY } from "@/routes";
import { BlogContentRenderer } from "./BlogContentRenderer";
import { BlogPostCard } from "./BlogPostCard";
import { BlogTocInline, BlogTocRail, shouldShowToc } from "./BlogToc";

const postJsonLd = (post: BlogPost) => {
  const category = getCategoryBySlug(post.category);

  return [
    blogPostingJsonLd({
      title: post.title,
      description: post.description,
      path: `/blog/${post.slug}`,
      datePublished: post.date,
      dateModified: post.updated,
      authorName: post.author,
      image: post.ogImage,
      section: category?.name,
    }),
    breadcrumbJsonLd([
      { name: "Blog", path: "/blog" },
      ...(category ? [{ name: category.name, path: `/blog/category/${category.slug}` }] : []),
      { name: post.title, path: `/blog/${post.slug}` },
    ]),
    // A single Q&A is not an FAQ; only emit the schema when the section is real.
    ...(post.faq.length >= 2 ? [faqPageJsonLd(post.faq)] : []),
  ];
};

const Breadcrumb = ({ post }: { post: BlogPost }) => {
  const category = getCategoryBySlug(post.category);

  return (
    <nav aria-label="Breadcrumb" className="flex items-center gap-1.5 text-sm text-muted-foreground">
      <Link to={BLOG()} className="transition-colors hover:text-foreground">
        Blog
      </Link>
      {category && (
        <>
          <ChevronRight className="h-3.5 w-3.5 shrink-0 text-foreground/30" />
          <Link
            to={BLOG_CATEGORY({ categorySlug: category.slug })}
            className="transition-colors hover:text-foreground"
          >
            {category.name}
          </Link>
        </>
      )}
    </nav>
  );
};

/** Freshness-forward dateline: "Updated" carries the visual weight when present. */
const Dateline = ({ post }: { post: BlogPost }) => (
  <p className="m-0 text-sm text-foreground/50">
    {post.updated && post.updated !== post.date && (
      <>
        <span className="font-medium text-foreground/75">Updated {formatPostDate(post.updated)}</span>
        {" · "}
      </>
    )}
    {post.author} · Published {formatPostDate(post.date)} · {post.readingMinutes} min read
  </p>
);

const RelatedPosts = ({ currentPost }: { currentPost: BlogPost }) => {
  const relatedPosts = getRelatedPosts(currentPost, 3);

  if (relatedPosts.length === 0) return null;

  return (
    <section className="flex flex-col gap-5 border-t border-foreground/10 pt-8">
      <div className="flex items-center justify-between gap-4">
        <h2 className="m-0 text-2xl font-semibold text-foreground md:text-3xl">More from the blog</h2>
        <Link to={BLOG()} className="whitespace-nowrap text-sm font-semibold text-brand-600 hover:underline">
          All posts
        </Link>
      </div>
      <div className="grid grid-cols-1 gap-x-5 gap-y-8 md:grid-cols-3">
        {relatedPosts.map(post => (
          <BlogPostCard key={post.slug} post={post} />
        ))}
      </div>
    </section>
  );
};

const BlogPostPage = () => {
  const { slug } = useParams<{ slug: string }>();
  const post = slug ? getPostBySlug(slug) : undefined;

  if (!post) {
    return <Navigate to={BLOG()} replace />;
  }

  const showToc = shouldShowToc(post.headings);

  return (
    <MarketingPageLayout surface="blog">
      <PageMeta
        title={`${post.seoTitle ?? post.title} | Stasht Blog`}
        description={post.description}
        path={`/blog/${post.slug}`}
        ogType="article"
        ogImage={post.ogImage ?? BLOG_OG_IMAGE}
        jsonLd={postJsonLd(post)}
      />
      <div
        className={`page-frame page-content ${
          showToc ? "xl:grid xl:grid-cols-[minmax(0,760px)_15rem] xl:gap-12" : ""
        }`}
      >
        <div className="reading-column flex min-w-0 flex-col gap-8">
          <Breadcrumb post={post} />

          <header className="flex flex-col gap-3">
            <h1 className="page-title m-0 text-foreground text-balance">
              {post.title}
            </h1>
            <Dateline post={post} />
          </header>

          {showToc && <BlogTocInline headings={post.headings} />}

          <BlogContentRenderer blocks={post.blocks} />

          <RelatedPosts currentPost={post} />

          <GetStashtCta surface="blog" />
        </div>

        {showToc && <BlogTocRail headings={post.headings} />}
      </div>
    </MarketingPageLayout>
  );
};

export default BlogPostPage;
